/**
 * @file messageService.js
 * @description Messaging service for the Inbox. Loads conversation lists, individual
 *   message threads, sends messages, marks conversations as read, and provides
 *   real-time Supabase subscriptions for live message updates.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/messageService
 */
import { supabase } from "./supabase";
import { createNotification } from "./notificationService";
import { attachCaseLabels } from "./caseLabelService";
import { logCaughtError } from "./errorService";
import { loadProviderNamesForAgency } from "./providerIdentity";

// Per-subscription counter. Realtime channel topics must be UNIQUE per live
// subscriber: supabase.channel(topic) returns an EXISTING channel if one with
// that topic is already registered, and calling .on() on an already-subscribed
// channel throws "cannot add postgres_changes callbacks ... after subscribe()".
// Multiple components (TopNav + Inbox + AgencyDashboard) legitimately watch the
// same user's messages at once, so we suffix each subscription with a unique
// token to keep their channels distinct.
let _msgChannelSeq = 0;
const nextChannelToken = () => `${++_msgChannelSeq}`;


/**
 * @description Derives a display state label and tone for a conversation based on
 *   the latest message and whether there are unread messages.
 * @param {Object} params
 * @param {Object|null} params.latestMessage - The latest message row, or null
 * @param {string} params.userId - The viewing user's ID
 * @param {string} params.counterpartType - "agency" or "therapist"
 * @param {boolean} params.hasUnread - Whether there are unread messages
 * @returns {{ state: string, label: string, tone: string }}
 */
export function deriveConversationState({ latestMessage, userId, counterpartType, hasUnread }) {
  if (!latestMessage) {
    return {
      state: "no_messages",
      label: "No messages yet",
      tone: "neutral",
    };
  }

  const counterpartLabel = counterpartType === "agency" ? "Agency" : "Provider";
  const lastFromUser = latestMessage.sender_id === userId;

  if (hasUnread && !lastFromUser) {
    return {
      state: `unread_from_${counterpartType}`,
      label: `Unread from ${counterpartLabel}`,
      tone: "attention",
    };
  }

  if (lastFromUser) {
    return {
      state: `awaiting_${counterpartType}`,
      label: `Awaiting ${counterpartLabel}`,
      tone: "muted",
    };
  }

  // Reaching here means the counterpart sent the last message and it's been read
  // — you still OWE a reply. (Was "Up to date"; aligned with the Inbox
  // "Needs reply" + Pulse "Replies owed" definition so a read-but-unanswered
  // thread reads as owed on every surface, not "all done".)
  return {
    state: `owes_reply_to_${counterpartType}`,
    label: `Reply to ${counterpartLabel}`,
    tone: "attention",
  };
}

// ── Outreach-only threads ────────────────────────────────────────────────────
// Direct Outreach / Reach Blast messages have no application yet (the
// therapist hasn't applied to the case being pitched), so they can't be
// addressed by application_id the way every other thread is. They're
// addressed instead by a synthetic id built from the (case_id, counterpart)
// pair — "oc:<caseId>:<counterpartId>" — everywhere a real thread would
// otherwise use its application id (loadMessages, sendMessage,
// markConversationRead, subscribeToMessages, and the notification this
// module writes when replying in one).
const OUTREACH_ID_PREFIX = "oc:";
function buildOutreachThreadId(caseId, counterpartId) {
  return `${OUTREACH_ID_PREFIX}${caseId}:${counterpartId}`;
}
function parseOutreachThreadId(id) {
  if (typeof id !== "string" || !id.startsWith(OUTREACH_ID_PREFIX)) return null;
  const [caseId, counterpartId] = id.slice(OUTREACH_ID_PREFIX.length).split(":");
  if (!caseId || !counterpartId) return null;
  return { caseId, counterpartId };
}

// ── Load conversations (applications with message metadata) ────────────────
// Each application is a potential conversation anchor — plus outreach-only
// threads (see above), which are grouped and merged in below.
/**
 * @description Loads all conversations for a user, enriched with the latest message
 *   and unread status for each application thread, plus any outreach-only
 *   threads (Direct Outreach / Reach Blast messages with no application yet).
 * @param {string} userId - The viewing user's ID
 * @param {string} role - "agency" or therapist role
 * @returns {Promise<Object[]>} Array of conversation objects
 */
export async function loadConversations(userId, role) {
  // client_initials is an agency-internal case reference — only fetch it for
  // agency viewers so it never reaches a therapist's payload (not just hidden
  // in the UI, never sent).
  const caseFields = role === "agency"
    ? "agency_id, agency_name, dba_name, type, setting, employment, city, state, client_initials, case_code"
    : "agency_id, agency_name, dba_name, type, setting, employment, city, state, case_code";
  // An agency reads its own cases directly (it owns the rows), so its embed keeps
  // client_initials. A provider cannot read the cases table: it gets the same case
  // facts, minus anything internal, from get_case_labels and they are re-attached
  // under the same `case` key below, so every mapper downstream is unchanged.
  const isAgency = role === "agency";
  // Load applications the user participates in
  let query = supabase
    .from("applications")
    .select(isAgency
      ? `
      id, case_id, therapist_id, status, created_at,
      case:cases(${caseFields})
    `
      : "id, case_id, therapist_id, status, created_at")
    .order("updated_at", { ascending: false });

  if (role === "agency") {
    // RLS handles filtering to agency's own cases
  } else {
    query = query.eq("therapist_id", userId);
  }

  const { data: appsRaw, error: appErr } = await query;
  if (appErr) throw appErr;
  const apps = isAgency ? (appsRaw || []) : await attachCaseLabels(appsRaw || [], "case");

  // Load latest message per application in one query
  const appIds = apps.map(a => a.id);
  let messages = [];
  if (appIds.length > 0) {
    const { data } = await supabase
      .from("messages")
      .select("id, application_id, sender_id, recipient_id, body, read, created_at")
      .in("application_id", appIds)
      .order("created_at", { ascending: false })
      .limit(500); // MIN-R5-4: cap to avoid unbounded fetch; only latest is used per conversation
    messages = data || [];
  }

  // Group messages by application and take the latest
  const latestByApp = {};
  const unreadByApp = {};
  for (const m of messages) {
    if (!latestByApp[m.application_id]) latestByApp[m.application_id] = m;
    if (m.recipient_id === userId && !m.read) unreadByApp[m.application_id] = true;
  }

  // Outreach-only messages (no application_id yet) — sender/recipient is
  // whichever side of the pair isn't `userId`; grouped by (case_id, that
  // other party) into one synthetic thread each. Ascending order so the
  // first message seen per group is genuinely the earliest.
  const outreachOrClause = role === "agency" ? `sender_id.eq.${userId}` : `recipient_id.eq.${userId}`;
  const { data: outreachMsgsRaw } = await supabase
    .from("messages")
    .select(isAgency
      ? `id, case_id, sender_id, recipient_id, body, read, created_at, case:cases(${caseFields})`
      : "id, case_id, sender_id, recipient_id, body, read, created_at")
    .is("application_id", null)
    .not("case_id", "is", null)
    .or(outreachOrClause)
    .order("created_at", { ascending: true })
    .limit(1000);
  const outreachMsgs = isAgency ? (outreachMsgsRaw || []) : await attachCaseLabels(outreachMsgsRaw || [], "case");
  const outreachGroups = {};
  for (const m of outreachMsgs) {
    const counterpartId = m.sender_id === userId ? m.recipient_id : m.sender_id;
    if (!counterpartId) continue;
    const key = `${m.case_id}:${counterpartId}`;
    if (!outreachGroups[key]) {
      outreachGroups[key] = { caseId: m.case_id, counterpartId, case: m.case || {}, messages: [] };
    }
    outreachGroups[key].messages.push(m);
  }

  // Load counterpart names — union of application counterparts and outreach
  // counterparts, so one profile fetch covers both.
  const appCounterpartIds = apps.map(a => role === "agency" ? a.therapist_id : a.case?.agency_id).filter(Boolean);
  const outreachCounterpartIds = Object.values(outreachGroups).map(g => g.counterpartId);
  const counterpartIds = [...new Set([...appCounterpartIds, ...outreachCounterpartIds])];

  // An agency names its provider counterparts through the server rule (full name once the provider engaged with it,
  // "First L." before, e.g. a thread the agency started): lib/providerIdentity. Providers read agency names directly.
  let profiles;
  if (role === "agency") {
    const names = await loadProviderNamesForAgency(counterpartIds);
    profiles = Object.entries(names).map(([id, n]) => ({ id, full_name: n.name, avatar_url: n.avatarUrl }));
  } else {
    ({ data: profiles } = await supabase
      .from("profiles")
      .select("id, full_name, avatar_url")
      .in("id", counterpartIds));
  }

  // For agency counterparts, also load logo_url from agency_profiles
  const agencyCounterpartIds = role !== "agency"
    ? [...new Set([
        ...apps.map(a => a.case?.agency_id).filter(Boolean),
        ...Object.values(outreachGroups).map(g => g.case?.agency_id).filter(Boolean),
      ])]
    : [];
  let agencyLogoMap = {};
  if (agencyCounterpartIds.length > 0) {
    const { data: agencyProfiles } = await supabase
      .from("agency_public_profiles")
      .select("user_id, logo_url")
      .in("user_id", agencyCounterpartIds);
    agencyLogoMap = Object.fromEntries((agencyProfiles || []).filter(a => a.logo_url).map(a => [a.user_id, a.logo_url]));
  }

  const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));

  // DB → UI status
  const statusMap = { pending: "new", reviewing: "reviewing", interview: "interested", offered: "interested", declined: "declined", withdrawn: "declined", closed: "declined" };

  // Per-user Archive state. A hide is "hidden as of hidden_at"; a thread with a
  // message NEWER than hidden_at auto-resurfaces (so archiving never drops a
  // reply). RLS scopes this to the caller's own rows.
  let hiddenAtByApp = {};
  if (appIds.length > 0) {
    const { data: hiddenRows } = await supabase
      .from("conversation_hidden")
      .select("application_id, hidden_at")
      .in("application_id", appIds);
    hiddenAtByApp = Object.fromEntries(
      (hiddenRows || []).map(h => [h.application_id, new Date(h.hidden_at).getTime()])
    );
  }

  // Outreach threads don't have a real applicant pipeline stage — "outreach"
  // is its own STATUS_META entry (Inbox.jsx) rather than reusing "new", which
  // would visually read as a fresh application that doesn't exist.
  const outreachThreads = Object.values(outreachGroups).map(g => {
    const c = g.case || {};
    const counterpartName = role === "agency"
      ? (profileMap[g.counterpartId]?.full_name || "Provider")
      : (c.dba_name || c.agency_name || "Agency");
    const msgs = g.messages; // ascending — last element is latest
    const latest = msgs[msgs.length - 1];
    const hasUnread = msgs.some(m => m.recipient_id === userId && !m.read);
    const conversationState = deriveConversationState({
      latestMessage: latest,
      userId,
      counterpartType: role === "agency" ? "therapist" : "agency",
      hasUnread,
    });
    return {
      id: buildOutreachThreadId(g.caseId, g.counterpartId),
      applicationId: null,
      caseId: g.caseId,
      caseCode: c.case_code || "",
      counterpart: counterpartName,
      counterpartType: role === "agency" ? "therapist" : "agency",
      counterpartId: g.counterpartId,
      counterpartAvatarUrl: profileMap[g.counterpartId]?.avatar_url || agencyLogoMap[g.counterpartId] || null,
      type: c.type || "RBT",
      setting: c.setting || "Home",
      city: c.city || "",
      state: c.state || "",
      clientInitials: c.client_initials || "",
      status: "outreach",
      needsResponse: !!latest && latest.sender_id !== userId,
      lastMessage: latest ? {
        from: latest.sender_id === userId
          ? (role === "agency" ? "agency" : "therapist")
          : (role === "agency" ? "therapist" : "agency"),
        text: latest.body,
        time: fmtTime(latest.created_at),
      } : null,
      lastUpdate: fmtDate(latest ? latest.created_at : g.messages[0]?.created_at),
      lastAt: (latest ? latest.created_at : g.messages[0]?.created_at) || null, // Inbox words it in the viewer's language
      lastActivityTs: new Date(latest ? latest.created_at : g.messages[0]?.created_at).getTime(),
      unread: hasUnread,
      // Not wired to conversation_hidden (that table keys on application_id)
      // — archiving an outreach thread is a follow-up, not part of this fix.
      hidden: false,
      conversationState: conversationState.state,
      conversationStateLabel: conversationState.label,
      conversationTone: conversationState.tone,
    };
  });

  const appThreads = apps.map(a => {
    const c = a.case || {};
    const counterpartId = role === "agency" ? a.therapist_id : c.agency_id;
    const counterpartName = role === "agency"
      ? (profileMap[counterpartId]?.full_name || "Provider")
      : (c.dba_name || c.agency_name || "Agency");
    const latest = latestByApp[a.id];
    const hasUnread = !!unreadByApp[a.id];
    const conversationState = deriveConversationState({
      latestMessage: latest,
      userId,
      counterpartType: role === "agency" ? "therapist" : "agency",
      hasUnread,
    });

    return {
      id: a.id,
      applicationId: a.id,
      caseId: a.case_id,   // lets "View Application" focus the applicant on the agency dashboard
      caseCode: c.case_code || "",   // human-friendly id shown on the tile + searchable
      counterpart: counterpartName,
      counterpartType: role === "agency" ? "therapist" : "agency",
      counterpartId,
      counterpartAvatarUrl: profileMap[counterpartId]?.avatar_url || agencyLogoMap[counterpartId] || null,
      type: c.type || "RBT",
      setting: c.setting || "Home",
      city: c.city || "",
      state: c.state || "",
      // Agency-internal client reference (e.g. "T.T."). Surfaced only on the
      // agency side of the inbox so a therapist never sees a client identifier.
      clientInitials: c.client_initials || "",
      status: statusMap[a.status] || "new",
      dbStatus: a.status, // the raw stage, for providerMessagingOpen
      // "Needs response" = the last message came from the counterpart, so the
      // ball is in the viewer's court (whether or not it's been read yet).
      needsResponse: !!latest && latest.sender_id !== userId,
      lastMessage: latest ? {
        from: latest.sender_id === userId
          ? (role === "agency" ? "agency" : "therapist")
          : (role === "agency" ? "therapist" : "agency"),
        text: latest.body,
        time: fmtTime(latest.created_at),
      } : null,
      lastUpdate: latest ? fmtDate(latest.created_at) : fmtDate(a.created_at),
      lastAt: (latest ? latest.created_at : a.created_at) || null, // Inbox words it in the viewer's language
      // Raw ms of last activity (latest message, else application created) so
      // the inbox can sort by recency / oldest-waiting client-side.
      lastActivityTs: new Date(latest ? latest.created_at : a.created_at).getTime(),
      unread: hasUnread,
      // Archived from THIS user's inbox — true only while no message has arrived
      // since they hid it (resurface-on-new-message).
      hidden: (() => {
        const h = hiddenAtByApp[a.id];
        if (h == null) return false;
        const lastTs = new Date(latest ? latest.created_at : a.created_at).getTime();
        return h >= lastTs;
      })(),
      conversationState: conversationState.state,
      conversationStateLabel: conversationState.label,
      conversationTone: conversationState.tone,
    };
  });

  return [...appThreads, ...outreachThreads];
}

// ── Archive (per-user hide) ─────────────────────────────────────────────────
// Conversations can't be deleted (messages are an audit record); a user hides a
// thread from their OWN inbox. Resurfaces automatically when a newer message
// arrives (loadConversations compares hidden_at to last activity).
export async function hideConversation(applicationId, userId) {
  if (!applicationId || !userId) return { ok: false, error: "missing_args" };
  const { error } = await supabase
    .from("conversation_hidden")
    .upsert({ application_id: applicationId, user_id: userId, hidden_at: new Date().toISOString() },
            { onConflict: "application_id,user_id" });
  return error ? { ok: false, error: error.message } : { ok: true };
}

export async function unhideConversation(applicationId, userId) {
  if (!applicationId || !userId) return { ok: false, error: "missing_args" };
  const { error } = await supabase
    .from("conversation_hidden")
    .delete()
    .eq("application_id", applicationId)
    .eq("user_id", userId);
  return error ? { ok: false, error: error.message } : { ok: true };
}

// ── Pulse rail: agency thread feed ────────────────────────────────────────
// Returns up to 5 threads shaped for ThreadsCard: unread-first, then newest.
// Three-step query mirrors loadConversations but is leaner — only the fields
// the Pulse surface needs, no conversation-state derivation.
export async function loadAgencyThreadsForPulse(userId) {
  if (!userId) return [];

  // 1. Most recent applications for this agency (RLS filters to own cases)
  const { data: apps, error } = await supabase
    .from("applications")
    .select("id, therapist_id, status, case:cases(type, client_initials, case_code)")
    .order("updated_at", { ascending: false })
    .limit(20);
  if (error || !apps?.length) return [];

  // 2. Latest message + unread stats per application
  const appIds = apps.map(a => a.id);
  const { data: messages } = await supabase
    .from("messages")
    .select("application_id, sender_id, recipient_id, body, read, created_at")
    .in("application_id", appIds)
    .order("created_at", { ascending: false })
    .limit(300);

  const latestByApp = {};
  const unreadByApp = {};
  const unreadCountByApp = {};
  for (const m of (messages || [])) {
    if (!latestByApp[m.application_id]) latestByApp[m.application_id] = m;
    if (m.recipient_id === userId && !m.read) {
      unreadByApp[m.application_id] = true;
      unreadCountByApp[m.application_id] = (unreadCountByApp[m.application_id] || 0) + 1;
    }
  }

  // 3. Therapist display names
  const therapistIds = [...new Set(apps.map(a => a.therapist_id).filter(Boolean))];
  const { data: profiles } = therapistIds.length
    ? await supabase.from("profiles").select("id, full_name").in("id", therapistIds)
    : { data: [] };
  const nameMap = Object.fromEntries((profiles || []).map(p => [p.id, p.full_name || "Provider"]));

  const statusRisk = {
    interview: { bonus: 18, label: "Interview stage", reason: "candidate is already at interview stage" },
    interview_sent: { bonus: 18, label: "Interview stage", reason: "candidate is already at interview stage" },
    offered: { bonus: 22, label: "Offer stage", reason: "candidate is already at offer stage" },
    offer_sent: { bonus: 22, label: "Offer stage", reason: "candidate is already at offer stage" },
    reviewing: { bonus: 10, label: "Review active", reason: "candidate is already in review" },
    pending: { bonus: 8, label: "New candidate", reason: "candidate is waiting on a first response" },
  };

  // Build + sort: highest reply risk first, then newest risk tie-break
  return apps
    .map(a => {
      const latest = latestByApp[a.id];
      if (!latest) return null;
      const hasUnread = !!unreadByApp[a.id];
      const unreadCount = unreadCountByApp[a.id] || 0;
      // "Owes a reply" = the last message is from the candidate, READ OR NOT —
      // the same definition the Inbox uses. (Pulse previously equated owed with
      // unread, so a message you'd read but not answered silently dropped to
      // "Follow up" and disappeared from "Waiting on you" while the Inbox still
      // counted it. Aligned per founder so both surfaces agree.)
      const owesReply = latest.sender_id !== userId;
      const ageMs = Date.now() - new Date(latest.created_at).getTime();
      const ageHours = ageMs / 3_600_000;
      const ageLabel =
        ageHours < 1   ? "just now" :
        ageHours < 24  ? `${Math.round(ageHours)}h ago` :
        ageHours < 168 ? `${Math.floor(ageHours / 24)}d ago` :
                         `${Math.floor(ageHours / 168)}w ago`;

      let replyRiskScore = 0;
      const riskReasons = [];
      const stageMeta = statusRisk[a.status] || null;

      if (owesReply) {
        replyRiskScore += 40;
        riskReasons.push(hasUnread ? "provider is waiting on you" : "you read this but haven't replied");
      }
      if (unreadCount >= 2) {
        replyRiskScore += 12;
        riskReasons.push(`${unreadCount} unread messages`);
      }
      if (ageHours >= 24) {
        replyRiskScore += 18;
        riskReasons.push("reply lag is over 24h");
      } else if (ageHours >= 12) {
        replyRiskScore += 10;
        riskReasons.push("reply lag is over 12h");
      } else if (ageHours >= 4) {
        replyRiskScore += 4;
      }
      if (stageMeta) {
        replyRiskScore += stageMeta.bonus;
        riskReasons.push(stageMeta.reason);
      }
      if (!hasUnread && latest.sender_id === userId) {
        replyRiskScore -= 18;
      }

      const riskLevel =
        replyRiskScore >= 60 ? "high" :
        replyRiskScore >= 30 ? "medium" :
        "low";
      // "Reply now" / "Needs reply" = the agency OWES a reply = the last message
      // is from the candidate (read or not) — same definition as the Inbox, so
      // the per-row badge and the "Waiting on you" count can't contradict it.
      // Once the agency has replied (last message is theirs), an aging thread is
      // a soft "Follow up", never an owed reply.
      const riskLabel =
        owesReply && riskLevel === "high" ? "Reply now" :
        owesReply                         ? "Needs reply" :
        riskLevel !== "low"               ? "Follow up" :
        "Monitor";

      return {
        id: a.id,
        sender: { name: nameMap[a.therapist_id] || "Provider" },
        context: (a.case?.type || "RBT") + " candidate",
        caseRef: a.case?.client_initials || "",
        caseCode: a.case?.case_code || "",
        preview: latest.body,
        ageHours,
        ageLabel,
        urgency: owesReply ? "warm" : "calm",
        unread: hasUnread,
        owesReply,
        unreadCount,
        applicationStatus: a.status || null,
        replyRiskScore,
        riskLevel,
        riskLabel,
        riskReason: riskReasons[0] || (hasUnread ? "waiting on your reply" : "recent thread"),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.replyRiskScore !== a.replyRiskScore) return b.replyRiskScore - a.replyRiskScore;
      if (a.unread !== b.unread) return a.unread ? -1 : 1;
      return a.ageHours - b.ageHours;
    })
    .slice(0, 5);
}

// ── Load messages for a specific application ───────────────────────────────
// MED-R6-3: Verify the caller participates in this application before fetching
// messages. This is a service-layer defence-in-depth check — RLS enforces the
// same constraint at the DB level, but if a policy regresses this check prevents
// silent cross-conversation reads.
/**
 * @description Loads all messages for a specific thread. Verifies the caller
 *   is a participant (therapist or agency) before fetching.
 * @param {string} threadId - Application UUID, or a synthetic outreach thread
 *   id ("oc:<caseId>:<counterpartId>") for an application-less thread
 * @param {string} userId - The viewing user's ID
 * @param {string} role - "agency" or therapist role
 * @returns {Promise<Object[]>} Array of formatted message objects
 */
export async function loadMessages(threadId, userId, role) {
  const oc = parseOutreachThreadId(threadId);
  if (oc) {
    // RLS on messages restricts rows to sender/recipient, so scoping to the
    // exact (userId, counterpart) pair on this case is both the authorization
    // check and the query — no separate participation lookup needed.
    const { data, error } = await supabase
      .from("messages")
      .select("id, sender_id, recipient_id, body, read, created_at, kind")
      .eq("case_id", oc.caseId)
      .is("application_id", null)
      .or(`and(sender_id.eq.${userId},recipient_id.eq.${oc.counterpartId}),and(sender_id.eq.${oc.counterpartId},recipient_id.eq.${userId})`)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return (data || []).map(m => ({
      id: m.id,
      from: m.sender_id === userId
        ? (role === "agency" ? "agency" : "therapist")
        : (role === "agency" ? "therapist" : "agency"),
      text: m.body,
      time: fmtTime(m.created_at),
      createdAt: m.created_at,
      read: m.read,
      kind: m.kind || "text",
      senderId: m.sender_id,
    }));
  }

  // Confirm participation: caller must be the therapist or the agency on the case
  const { data: appRow, error: appErr } = await supabase
    .from("applications")
    .select("therapist_id, case:cases(agency_id)")
    .eq("id", threadId)
    .maybeSingle();

  if (appErr || !appRow) throw new Error("Application not found.");

  const therapistId = appRow.therapist_id;
  const agencyId    = appRow.case?.agency_id;
  const isParticipant = userId === therapistId || userId === agencyId;
  if (!isParticipant) throw new Error("Not authorized to read this conversation.");

  const { data, error } = await supabase
    .from("messages")
    .select("id, sender_id, recipient_id, body, read, created_at, kind")
    .eq("application_id", threadId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return (data || []).map(m => ({
    id: m.id,
    from: m.sender_id === userId
      ? (role === "agency" ? "agency" : "therapist")
      : (role === "agency" ? "therapist" : "agency"),
    text: m.body,
    time: fmtTime(m.created_at),
    createdAt: m.created_at,   // drives the thread's day separators
    read: m.read,
    kind: m.kind || "text",    // resume_request / resume_added render as cards (components/resume/ResumeMessageCard)
    senderId: m.sender_id,
  }));
}

// ── Send a message ─────────────────────────────────────────────────────────
/**
 * @description Sends a message in an application thread. Enforces a 5000-character
 *   body limit matching the DB CHECK constraint.
 * @param {string} threadId - Application UUID, or a synthetic outreach thread
 *   id ("oc:<caseId>:<counterpartId>") when replying to an outreach-only thread
 * @param {string} senderId - Sender's user ID
 * @param {string} recipientId - Recipient's user ID
 * @param {string} body - Message text
 * @returns {Promise<Object>} The inserted message row
 */
export async function sendMessage(threadId, senderId, recipientId, body) {
  // MED-R5-1: hard cap matches the DB CHECK constraint (char_length <= 5000)
  const safeBody = String(body || "").trim().slice(0, 5000);
  const oc = parseOutreachThreadId(threadId);
  const insertRow = oc
    ? { case_id: oc.caseId, sender_id: senderId, recipient_id: recipientId, body: safeBody }
    : { application_id: threadId, sender_id: senderId, recipient_id: recipientId, body: safeBody };
  const { data, error } = await supabase
    .from("messages")
    .insert(insertRow)
    .select()
    .single();

  if (error) {
    // Only the thread id and the database's own error text are logged: never the message body.
    logCaughtError(error, "message_send_failed", { threadId, step: "insert" });
    throw error;
  }

  // In-app notification to recipient — non-fatal, deduped per conversation
  // so the bell only rings once per thread until the recipient opens the inbox.
  // link_data uses `threadId`, not `applicationId` — that's the key
  // notificationService.js's normalizeNotification/applyNotificationClick
  // actually reads for the "inbox" view target (was a latent mismatch here;
  // corrected while touching this function for outreach-thread support).
  createNotification({
    user_id:    recipientId,
    type:       "new_message",
    title:      "New message",
    body:       safeBody.slice(0, 120) + (safeBody.length > 120 ? "…" : ""),
    link_data:  { view: "inbox", threadId },
    dedupe_key: `new_message:${recipientId}:${threadId}`,
    category:   "messages",
  }).catch(err => console.warn("[sendMessage] Notification failed (non-fatal):", err?.message)); // message already sent

  return data;
}

// ── Mark messages as read ──────────────────────────────────────────────────
export async function markConversationRead(threadId, userId) {
  const oc = parseOutreachThreadId(threadId);
  if (oc) {
    const { error } = await supabase
      .from("messages")
      .update({ read: true })
      .eq("case_id", oc.caseId)
      .is("application_id", null)
      .eq("recipient_id", userId)
      .eq("sender_id", oc.counterpartId)
      .eq("read", false);
    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from("messages")
    .update({ read: true })
    .eq("application_id", threadId)
    .eq("recipient_id", userId)
    .eq("read", false);

  if (error) throw error;
}

// ── Lightweight unread count for nav badges ────────────────────────────────
export async function getUnreadConversationCount(userId, role) {
  // Only the application ids are used below (the embedded case was never read),
  // so no cases embed: a provider cannot read the cases table.
  let appsQuery = supabase
    .from("applications")
    .select("id");

  if (role !== "agency") {
    appsQuery = appsQuery.eq("therapist_id", userId);
  }

  const { data: apps, error: appsErr } = await appsQuery;
  if (appsErr) throw appsErr;

  const appIds = (apps || []).map(a => a.id).filter(Boolean);

  let appUnreadIds = [];
  if (appIds.length > 0) {
    const { data: unreadRows, error: unreadErr } = await supabase
      .from("messages")
      .select("application_id")
      .eq("recipient_id", userId)
      .eq("read", false)
      .in("application_id", appIds)
      .limit(500);
    if (unreadErr) throw unreadErr;
    appUnreadIds = (unreadRows || []).map(r => r.application_id).filter(Boolean);
  }

  // Outreach threads (no application) — only ever unread for the recipient
  // side (therapists), since an agency is always the sender there. Counted
  // as distinct (case_id, sender_id) pairs to match one badge tick per thread.
  const { data: outreachUnreadRows, error: outreachErr } = await supabase
    .from("messages")
    .select("case_id, sender_id")
    .eq("recipient_id", userId)
    .eq("read", false)
    .is("application_id", null)
    .not("case_id", "is", null)
    .limit(500);
  if (outreachErr) throw outreachErr;
  const outreachUnreadKeys = (outreachUnreadRows || []).map(r => `${r.case_id}:${r.sender_id}`);

  return new Set(appUnreadIds).size + new Set(outreachUnreadKeys).size;
}

// ── Realtime subscription for a conversation ───────────────────────────────
// Returns an unsubscribe function — call it in useEffect cleanup.
/**
 * @description Subscribes to real-time INSERT/UPDATE events on a single thread.
 * @param {string} threadId - Application UUID, or a synthetic outreach thread
 *   id ("oc:<caseId>:<counterpartId>") for an application-less thread
 * @param {Function} onNewMessage - Callback receiving the new message row
 * @returns {Function} Unsubscribe function — call in useEffect cleanup
 */
export function subscribeToMessages(threadId, onNewMessage) {
  const oc = parseOutreachThreadId(threadId);
  const tok = nextChannelToken();

  if (oc) {
    // Realtime postgres_changes filters only support one equality clause, so
    // this subscribes on case_id alone and filters client-side to the exact
    // counterpart pair — a case can have outreach threads with more than one
    // therapist, and this must not cross-talk between them.
    const matches = (m) => !m.application_id && (m.sender_id === oc.counterpartId || m.recipient_id === oc.counterpartId);
    const channel = supabase
      .channel(`messages:oc:${oc.caseId}:${oc.counterpartId}:${tok}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `case_id=eq.${oc.caseId}` },
        (payload) => { if (matches(payload.new)) onNewMessage(payload.new, "INSERT"); }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "messages", filter: `case_id=eq.${oc.caseId}` },
        (payload) => { if (matches(payload.new)) onNewMessage(payload.new, "UPDATE"); }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }

  const channel = supabase
    .channel(`messages:app:${threadId}:${tok}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `application_id=eq.${threadId}`,
      },
      (payload) => onNewMessage(payload.new, "INSERT")
    )
    // UPDATE catches the read-flag flip so the SENDER's open thread updates its
    // delivery/read receipts (grey ✓✓ → blue ✓✓) the moment the recipient reads.
    // The event type is passed through so consumers can patch in place rather
    // than re-fetching the whole conversation list on every read (egress!).
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "messages",
        filter: `application_id=eq.${threadId}`,
      },
      (payload) => onNewMessage(payload.new, "UPDATE")
    )
    .subscribe();

  return () => supabase.removeChannel(channel);
}

/**
 * @description Subscribes to all incoming, outgoing, and read-update events for a
 *   user's inbox. Returns an unsubscribe function.
 * @param {string} userId - The user to watch messages for
 * @param {Function} onMessageChange - Callback receiving the new/updated message row
 * @returns {Function} Unsubscribe function — call in useEffect cleanup
 */
export function subscribeToInboxMessages(userId, onMessageChange) {
  const tok = nextChannelToken();
  const incoming = supabase
    .channel(`messages:incoming:${userId}:${tok}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `recipient_id=eq.${userId}`,
      },
      (payload) => onMessageChange(payload.new, "INSERT")
    )
    .subscribe();

  const outgoing = supabase
    .channel(`messages:outgoing:${userId}:${tok}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `sender_id=eq.${userId}`,
      },
      (payload) => onMessageChange(payload.new, "INSERT")
    )
    .subscribe();

  const readUpdates = supabase
    .channel(`messages:reads:${userId}:${tok}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "messages",
        filter: `recipient_id=eq.${userId}`,
      },
      (payload) => onMessageChange(payload.new, "UPDATE")
    )
    .subscribe();

  return () => {
    supabase.removeChannel(incoming);
    supabase.removeChannel(outgoing);
    supabase.removeChannel(readUpdates);
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────
// Time-of-day only ("2:14 PM"). The thread renders day separators, so the bubble
// timestamp doesn't need to repeat the date.
function fmtTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h % 12 || 12}:${m} ${h >= 12 ? "PM" : "AM"}`;
}

// Relative, inbox-style: today → time, yesterday → "Yesterday", this week →
// weekday, older → "Mon D". Makes the list read like a real messaging app.
function fmtDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
  if (dayDiff <= 0) {
    const h = d.getHours(), m = String(d.getMinutes()).padStart(2, "0");
    return `${h % 12 || 12}:${m} ${h >= 12 ? "PM" : "AM"}`;
  }
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff < 7)   return d.toLocaleString("en-US", { weekday: "short" });
  return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}`;
}
